import { useRef, useState, useEffect, useCallback } from "react";
import { Upload, Loader2, Sparkles, X, ZoomIn } from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE, formatApiError } from "@/lib/api";

export default function ImageUploader({
  value,
  onChange,
  label = "Imagem",
  withAIGenerate = true,
  aiPromptHint = "",
  compact = false,
}) {
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [showPrompt, setShowPrompt] = useState(false);
  const [prompt, setPrompt] = useState("");
  const inputRef = useRef(null);

  // Crop modal state
  const [cropSrc, setCropSrc] = useState("");
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef(null);
  const imgNatural = useRef({ w: 0, h: 0 });
  const CROP_SIZE = 360; // px (visual)
  const OUTPUT_SIZE = 1000; // px (saved)

  const previewSrc = !value
    ? ""
    : value.startsWith("http") || value.startsWith("data:")
      ? value
      : `${API_BASE.replace(/\/api$/, "")}${value}`;

  const openCropper = (file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        imgNatural.current = { w: img.naturalWidth, h: img.naturalHeight };
        setZoom(1);
        setOffset({ x: 0, y: 0 });
        setCropSrc(e.target.result);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  };

  const handleFiles = (fileList) => {
    const file = fileList?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Apenas imagens são aceitas.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Imagem maior que 10 MB.");
      return;
    }
    openCropper(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handleFiles(e.dataTransfer.files);
  };

  // Drag the image inside the crop frame
  const onPointerDown = (e) => {
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      origX: offset.x,
      origY: offset.y,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!dragRef.current) return;
    setOffset({
      x: dragRef.current.origX + (e.clientX - dragRef.current.startX),
      y: dragRef.current.origY + (e.clientY - dragRef.current.startY),
    });
  };
  const onPointerUp = (e) => {
    dragRef.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {}
  };

  const confirmCrop = useCallback(async () => {
    const { w, h } = imgNatural.current;
    if (!w || !h) return;
    // Calculate cover-fit scale at zoom=1
    const baseScale = Math.max(CROP_SIZE / w, CROP_SIZE / h);
    const scale = baseScale * zoom;
    // The image in DOM is drawn at w*scale x h*scale centered around the crop square,
    // then translated by offset. Convert to source-coords for canvas drawImage.
    // We want a 1:1 crop covering the visible CROP_SIZE square.
    const cropPxOnSource = CROP_SIZE / scale;
    // Source top-left (before offset, image is centered)
    const sx = (w - cropPxOnSource) / 2 - offset.x / scale;
    const sy = (h - cropPxOnSource) / 2 - offset.y / scale;
    const sxClamped = Math.max(0, Math.min(w - cropPxOnSource, sx));
    const syClamped = Math.max(0, Math.min(h - cropPxOnSource, sy));

    const canvas = document.createElement("canvas");
    canvas.width = OUTPUT_SIZE;
    canvas.height = OUTPUT_SIZE;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingQuality = "high";
    const img = new Image();
    img.src = cropSrc;
    await new Promise((res) => { img.onload = res; });
    ctx.drawImage(img, sxClamped, syClamped, cropPxOnSource, cropPxOnSource, 0, 0, OUTPUT_SIZE, OUTPUT_SIZE);
    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.92));
    if (!blob) {
      toast.error("Falha ao recortar");
      return;
    }
    const fd = new FormData();
    fd.append("file", new File([blob], "crop.jpg", { type: "image/jpeg" }));
    setUploading(true);
    setCropSrc("");
    try {
      const { data } = await api.post("/admin/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
      toast.success("Imagem enviada");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha no upload");
    } finally {
      setUploading(false);
    }
  }, [zoom, offset, cropSrc, onChange]);

  const generateAI = async () => {
    const finalPrompt = (prompt || aiPromptHint || "").trim();
    if (!finalPrompt || finalPrompt.length < 5) {
      toast.error("Descreva a imagem que deseja gerar.");
      return;
    }
    setGenerating(true);
    try {
      const { data } = await api.post("/admin/generate-image", { prompt: finalPrompt });
      onChange(data.url);
      setShowPrompt(false);
      setPrompt("");
      toast.success("Imagem gerada");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Falha na geração");
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div data-testid="image-uploader">
      {label && (
        <div className="flex items-center justify-between mb-1">
          <label className="label-x">{label}</label>
          {withAIGenerate && !previewSrc && (
            <button
              type="button"
              onClick={() => setShowPrompt((s) => !s)}
              className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.22em] text-blush hover:text-ink transition-colors"
              data-testid="image-uploader-ai-toggle"
            >
              <Sparkles size={12} /> Gerar com IA
            </button>
          )}
        </div>
      )}

      {previewSrc ? (
        compact ? (
          <div className="relative w-24 h-24 rounded-full overflow-hidden group mt-1 cursor-pointer border-2 border-gray-200">
            <img src={previewSrc} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center gap-1">
              <button type="button" onClick={() => inputRef.current?.click()}
                className="text-white text-[9px] uppercase tracking-wider font-semibold"
                data-testid="image-uploader-replace">Trocar</button>
              <button type="button" onClick={() => onChange("")}
                className="text-red-300 text-[9px] uppercase tracking-wider font-semibold"
                data-testid="image-uploader-remove">Remover</button>
            </div>
          </div>
        ) : (
        <div className="relative mt-1 border border-line group">
          <img src={previewSrc} alt="" className="w-full aspect-square object-cover" />
          <div className="absolute inset-0 bg-ink/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="bg-ivory text-ink px-4 py-2 text-[11px] uppercase tracking-[0.22em] hover:bg-blush hover:text-ivory transition-colors"
              data-testid="image-uploader-replace"
            >
              Trocar
            </button>
            <button
              type="button"
              onClick={() => onChange("")}
              className="bg-ivory text-ink px-4 py-2 text-[11px] uppercase tracking-[0.22em] hover:bg-red-500 hover:text-ivory transition-colors"
              data-testid="image-uploader-remove"
            >
              Remover
            </button>
          </div>
        </div>
        )
      ) : (
        <>
          {showPrompt && withAIGenerate ? (
            <div className="mt-1 border border-line bg-bone/40 p-4" data-testid="image-uploader-ai-panel">
              <label className="block text-[10px] uppercase tracking-[0.22em] text-graphite mb-2">
                Descreva a imagem
              </label>
              <textarea
                rows={3}
                value={prompt}
                onChange={(e) => setPrompt(e.target.value.slice(0, 1500))}
                placeholder={aiPromptHint || "Ex: estética facial editorial, tons quentes..."}
                className="w-full border border-line bg-ivory px-3 py-2 text-sm text-ink focus:outline-none focus:border-blush resize-none"
                data-testid="image-uploader-ai-prompt"
              />
              <div className="flex gap-2 mt-3">
                <button
                  type="button"
                  disabled={generating}
                  onClick={generateAI}
                  className="inline-flex items-center gap-1.5 bg-blush text-ivory px-4 py-2 text-[11px] uppercase tracking-[0.22em] hover:bg-ink transition-colors disabled:opacity-60"
                  data-testid="image-uploader-ai-submit"
                >
                  {generating ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
                  {generating ? "Gerando..." : "Gerar"}
                </button>
                <button
                  type="button"
                  onClick={() => { setShowPrompt(false); setPrompt(""); }}
                  className="text-[11px] uppercase tracking-[0.22em] text-graphite hover:text-ink"
                >
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <div
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              className={`mt-1 cursor-pointer border-2 border-dashed flex flex-col items-center justify-center gap-2 py-10 px-6 text-center transition-colors ${
                dragging ? "border-blush bg-blush/5" : "border-line bg-bone/40 hover:border-blush/60"
              }`}
              data-testid="image-uploader-dropzone"
            >
              {uploading ? (
                <Loader2 size={22} className="text-blush animate-spin" />
              ) : (
                <Upload size={22} className="text-graphite" />
              )}
              <p className="text-xs text-graphite">
                {uploading ? "Enviando..." : (
                  <>
                    <span className="font-medium text-ink">Clique</span> ou arraste uma imagem aqui
                  </>
                )}
              </p>
              <p className="text-[10px] uppercase tracking-[0.22em] text-graphite/70">
                JPG, PNG, WEBP — máx 5 MB
              </p>
              {withAIGenerate && !uploading && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setShowPrompt(true); }}
                  className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] font-medium rounded-md border border-blue-600 text-blue-600 bg-white hover:bg-blue-600 hover:text-white transition-colors"
                  data-testid="image-uploader-ai-cta"
                >
                  <Sparkles size={12} /> Criar imagem com IA
                </button>
              )}
            </div>
          )}
        </>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid="image-uploader-input"
      />

      {cropSrc && (
        <div className="fixed inset-0 z-50 bg-ink/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="image-cropper-modal">
          <div className="bg-ivory max-w-md w-full p-6 relative">
            <button
              type="button"
              onClick={() => setCropSrc("")}
              className="absolute top-3 right-3 text-ink/60 hover:text-ink"
              data-testid="cropper-close"
            >
              <X size={18} />
            </button>
            <h3 className="font-serif text-lg mb-4">Recortar imagem</h3>
            <p className="text-xs text-graphite mb-3">Arraste pra reposicionar e use o zoom. O recorte é quadrado (1:1).</p>

            <div
              className="relative mx-auto overflow-hidden border border-line touch-none select-none cursor-grab active:cursor-grabbing bg-ink/10"
              style={{ width: CROP_SIZE, height: CROP_SIZE }}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={onPointerUp}
              onPointerCancel={onPointerUp}
            >
              {(() => {
                const { w, h } = imgNatural.current;
                if (!w) return null;
                const baseScale = Math.max(CROP_SIZE / w, CROP_SIZE / h);
                const finalScale = baseScale * zoom;
                const dw = w * finalScale;
                const dh = h * finalScale;
                return (
                  <img
                    src={cropSrc}
                    alt="crop"
                    draggable={false}
                    style={{
                      position: "absolute",
                      width: dw,
                      height: dh,
                      left: (CROP_SIZE - dw) / 2 + offset.x,
                      top: (CROP_SIZE - dh) / 2 + offset.y,
                      maxWidth: "none",
                      pointerEvents: "none",
                    }}
                  />
                );
              })()}
            </div>

            <div className="mt-4 flex items-center gap-3">
              <ZoomIn size={14} className="text-graphite" />
              <input
                type="range"
                min={1}
                max={4}
                step={0.05}
                value={zoom}
                onChange={(e) => setZoom(parseFloat(e.target.value))}
                className="flex-1"
                data-testid="cropper-zoom"
              />
              <span className="text-xs text-graphite w-10 text-right">{Math.round(zoom * 100)}%</span>
            </div>

            <div className="mt-6 flex gap-3 justify-end">
              <button
                type="button"
                onClick={() => setCropSrc("")}
                className="px-4 py-2 text-[11px] uppercase tracking-[0.22em] text-graphite hover:text-ink"
                data-testid="cropper-cancel"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={confirmCrop}
                disabled={uploading}
                className="bg-ink text-ivory px-5 py-2 text-[11px] uppercase tracking-[0.22em] hover:bg-blush transition-colors disabled:opacity-50"
                data-testid="cropper-confirm"
              >
                {uploading ? "Enviando..." : "Recortar e enviar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
